import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Pencil, Trash2, Loader2, FileText, Eye, Code, Tag, CheckCircle, Upload, FileSpreadsheet } from 'lucide-react';
import api from '../services/api';
import { BACKEND_URL } from '../lib/config';

export default function TemplateManagement() {
  const [templates, setTemplates] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // null | 'create' | 'edit' | 'preview'
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: '', html_content: '', batch_id: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [uploadMode, setUploadMode] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [excelSuggestions, setExcelSuggestions] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);

  // BACKEND_URL, axios, all set* are stable module/useState refs — empty deps is correct.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, b] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/templates`),
        axios.get(`${BACKEND_URL}/api/batches`),
      ]);
      setTemplates(t.data);
      setBatches(b.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ name: '', html_content: '', batch_id: '', description: '' });
    setError(''); setSuccess('');
    setDialog('create');
  };

  const openEdit = async (tpl) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/templates/${tpl.id}`);
      setSelected(res.data);
      setForm({ name: res.data.name, html_content: res.data.html_content, batch_id: res.data.batch_id || '', description: res.data.description || '' });
    } catch { setForm({ name: tpl.name, html_content: '', batch_id: tpl.batch_id || '', description: tpl.description || '' }); }
    setError(''); setSuccess('');
    setDialog('edit');
  };

  const openPreview = async (tpl) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/templates/${tpl.id}`);
      setPreviewHtml(res.data.html_content || '');
    } catch { setPreviewHtml('<p>Preview unavailable</p>'); }
    setDialog('preview');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm(p => ({ ...p, html_content: ev.target.result, name: p.name || file.name.replace('.html', '') }));
    };
    reader.readAsText(file);
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExcelFile(file);
    setExcelSuggestions(null);
  };

  const analyzeExcel = async () => {
    if (!excelFile) return;
    setExcelLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', excelFile);
      // If we have a template ID (edit mode), pass it for pre-mapping
      if (selected?.id) {
        fd.append('template_id', selected.id);
      }
      const res = await api.placeholdersFromExcel(fd);
      setExcelSuggestions(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to analyze Excel file');
    } finally {
      setExcelLoading(false);
    }
  };

  const applyExcelPlaceholders = async () => {
    if (!excelFile) return;
    if (!form.html_content) { setError('Please add HTML content first'); return; }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const fd = new FormData();
      fd.append('file', excelFile);
      if (selected?.id) {
        fd.append('template_id', selected.id);
      } else {
        fd.append('html_content', form.html_content);
      }
      const res = await api.applyExcelPlaceholders(fd);
      const inserted = res.data.inserted_placeholders?.length || 0;
      const unmatched = res.data.unmatched_columns?.length || 0;
      setSuccess(`Auto-inserted ${inserted} placeholders!` + (unmatched ? ` ${unmatched} unmatched.` : ''));

      // Update the HTML content with the modified version
      const newHtml = res.data.modified_html || res.data.template?.html_content;
      if (newHtml) {
        setForm(p => ({ ...p, html_content: newHtml }));
      }
      // If editing, reload the template
      if (selected?.id) {
        const updated = await api.getTemplate(selected.id);
        setForm(p => ({ ...p, html_content: updated.data.html_content }));
      }
      setExcelSuggestions(null);
      setExcelFile(null);
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to apply placeholders');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!form.name || !form.html_content) { setError('Template name and HTML content required'); return; }
    setSaving(true); setError('');
    try {
      if (dialog === 'create') {
        await axios.post(`${BACKEND_URL}/api/admin/templates`, form);
        setSuccess('Template created');
      } else {
        await axios.put(`${BACKEND_URL}/api/admin/templates/${selected.id}`, form);
        setSuccess('Template updated');
      }
      load();
      setTimeout(() => setDialog(null), 1200);
    } catch (e) { setError(e?.response?.data?.detail || 'Failed to save template'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await axios.delete(`${BACKEND_URL}/api/admin/templates/${id}`);
    load();
  };

  const extractPlaceholders = (html) => {
    const matches = html.match(/\{([A-Za-z_][A-Za-z0-9_ .]*)\}/g) || [];
    return [...new Set(matches)];
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Templates</h1>
            <p className="text-sm text-muted-foreground">Manage HTML evaluation card templates</p>
          </div>
          <Button data-testid="add-template-btn" onClick={openCreate} className="bg-[#2BB5D8] hover:bg-[#25a3c4] text-[#0D1117] font-semibold">
            <Plus size={16} className="mr-1" /> New Template
          </Button>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-40 bg-[#111827] rounded-xl animate-pulse" />)}</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {templates.map(tpl => {
              const batch = batches.find(b => b.id === tpl.batch_id);
              return (
                <Card key={tpl.id} className="bg-[#111827] border-border/60 card-hover">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-9 h-9 bg-purple-900/30 rounded-lg flex items-center justify-center">
                          <FileText size={16} className="text-purple-400" />
                        </div>
                        <div>
                          <p className="font-semibold text-sm">{tpl.name}</p>
                          <p className="text-xs text-muted-foreground">v{tpl.version}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className={tpl.is_active ? 'border-green-500/30 text-green-400' : 'border-border text-muted-foreground'}>
                        {tpl.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>

                    {tpl.description && <p className="text-xs text-muted-foreground mb-2">{tpl.description}</p>}

                    {batch && (
                      <div className="flex items-center gap-1 mb-3">
                        <Tag size={11} className="text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Assigned to: {batch.name}</span>
                      </div>
                    )}

                    {tpl.placeholders?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-[10px] text-muted-foreground mb-1">Placeholders ({tpl.placeholders.length})</p>
                        <div className="flex flex-wrap gap-1">
                          {tpl.placeholders.slice(0, 6).map(p => (
                            <span key={p} className="text-[10px] bg-secondary/60 rounded px-1.5 py-0.5 font-mono">{'{' + p + '}'}</span>
                          ))}
                          {tpl.placeholders.length > 6 && (
                            <span className="text-[10px] text-muted-foreground">+{tpl.placeholders.length - 6} more</span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-1 mt-2">
                      <Button variant="ghost" size="sm" className="flex-1 text-xs" onClick={() => openPreview(tpl)}><Eye size={12} className="mr-1" /> Preview</Button>
                      <Button variant="ghost" size="sm" className="flex-1 text-xs" onClick={() => openEdit(tpl)}><Pencil size={12} className="mr-1" /> Edit</Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive/70 hover:text-destructive" onClick={() => handleDelete(tpl.id)}><Trash2 size={14} /></Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialog === 'create' || dialog === 'edit'} onOpenChange={v => !v && setDialog(null)}>
          <DialogContent className="bg-[#111827] border-border/60 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{dialog === 'create' ? 'New Template' : 'Edit Template'}</DialogTitle>
            </DialogHeader>
            {error && <Alert variant="destructive" className="bg-destructive/10 border-destructive/30"><AlertDescription>{error}</AlertDescription></Alert>}
            {success && <Alert className="bg-green-900/30 border-green-700/30 text-green-400"><AlertDescription>{success}</AlertDescription></Alert>}

            <Tabs defaultValue="editor">
              <TabsList className="bg-secondary/50">
                <TabsTrigger value="editor"><Code size={13} className="mr-1" />HTML Editor</TabsTrigger>
                <TabsTrigger value="upload"><Upload size={13} className="mr-1" />Upload File</TabsTrigger>
                <TabsTrigger value="excel"><FileSpreadsheet size={13} className="mr-1" />Import Excel</TabsTrigger>
                <TabsTrigger value="info">Info</TabsTrigger>
              </TabsList>

              <TabsContent value="editor" className="space-y-3">
                <div>
                  <Label>Template Name</Label>
                  <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="mt-1 bg-secondary/50 border-border/60" placeholder="e.g. Beginners Evaluation Card" />
                </div>
                <div>
                  <Label>HTML Content</Label>
                  <Textarea
                    value={form.html_content}
                    onChange={e => setForm(p => ({...p, html_content: e.target.value}))}
                    className="mt-1 bg-secondary/50 border-border/60 font-mono text-xs min-h-[300px]"
                    placeholder="Paste your HTML template here. Use {Placeholder} syntax..."
                  />
                </div>
                {form.html_content && (
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Detected placeholders:</p>
                    <div className="flex flex-wrap gap-1">
                      {extractPlaceholders(form.html_content).map(p => (
                        <span key={p} className="text-xs bg-primary/20 text-primary rounded px-1.5 py-0.5 font-mono">{'{' + p + '}'}</span>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="upload" className="space-y-3">
                <div className="border-2 border-dashed border-border/60 rounded-xl p-8 text-center">
                  <FileText size={32} className="mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground mb-3">Upload an HTML file</p>
                  <label className="cursor-pointer">
                    <Button variant="outline" asChild>
                      <span><Upload size={14} className="mr-2" /> Choose HTML File</span>
                    </Button>
                    <input type="file" accept=".html,.htm" className="hidden" onChange={handleFileUpload} />
                  </label>
                  {form.html_content && (
                    <p className="text-xs text-green-400 mt-3 flex items-center justify-center gap-1">
                      <CheckCircle size={12} /> HTML loaded ({form.html_content.length} chars)
                    </p>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="excel" className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Upload an Excel evaluation file to auto-generate matching <code className="text-xs bg-secondary/50 px-1 rounded">{'{Placeholder}'}</code> tokens for this template.
                </p>
                <div className="border-2 border-dashed border-border/60 rounded-xl p-6 text-center">
                  <FileSpreadsheet size={32} className="mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground mb-3">
                    {excelFile ? excelFile.name : 'Upload an .xlsx evaluation file'}
                  </p>
                  <label className="cursor-pointer">
                    <Button variant="outline" asChild>
                      <span><Upload size={14} className="mr-2" /> Choose Excel File</span>
                    </Button>
                    <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleExcelUpload} />
                  </label>
                  {excelFile && (
                    <div className="mt-3">
                      <Button
                        onClick={analyzeExcel}
                        disabled={excelLoading}
                        className="bg-[#2BB5D8] hover:bg-[#25a3c4] text-[#0D1117] font-semibold"
                      >
                        {excelLoading && <Loader2 size={14} className="animate-spin mr-1" />}
                        Analyze Excel
                      </Button>
                    </div>
                  )}
                </div>

                {excelSuggestions && (
                  <div className="space-y-3">
                    <div className="p-3 bg-secondary/30 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-2">
                        Found {excelSuggestions.excel_columns?.length || 0} columns
                        {excelSuggestions.existing_template_placeholders && (
                          <span> • Template has {excelSuggestions.existing_template_placeholders.length} existing placeholders</span>
                        )}
                      </p>
                      <div className="max-h-48 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-muted-foreground border-b border-border/40">
                              <th className="pb-1 pr-2">Excel Column</th>
                              <th className="pb-1 pr-2">Suggested Placeholder</th>
                              <th className="pb-1">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(excelSuggestions.suggested_placeholders).map(([col, placeholder]) => {
                              const isMapped = excelSuggestions.pre_mapped
                                ? excelSuggestions.pre_mapped[col] !== placeholder
                                : false;
                              const alreadyExists = excelSuggestions.existing_template_placeholders?.includes(placeholder);
                              return (
                                <tr key={col} className="border-b border-border/20">
                                  <td className="py-1 pr-2 font-medium">{col}</td>
                                  <td className="py-1 pr-2 font-mono text-[10px]">
                                    {'{' + placeholder + '}'}
                                  </td>
                                  <td className="py-1">
                                    {alreadyExists ? (
                                      <Badge variant="outline" className="border-green-500/30 text-green-400 text-[10px]">Exists</Badge>
                                    ) : isMapped ? (
                                      <Badge variant="outline" className="border-amber-500/30 text-amber-400 text-[10px]">New</Badge>
                                    ) : (
                                      <Badge variant="outline" className="border-blue-500/30 text-blue-400 text-[10px]">Suggested</Badge>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <Button
                      onClick={applyExcelPlaceholders}
                      disabled={saving}
                      className="w-full bg-green-700 hover:bg-green-600 text-white font-semibold"
                    >
                      {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <CheckCircle size={14} className="mr-1" />}
                      Auto-Insert Placeholders into HTML
                    </Button>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="info" className="space-y-3">
                <div>
                  <Label>Description</Label>
                  <Textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} className="mt-1 bg-secondary/50 border-border/60" rows={3} placeholder="Describe this template..." />
                </div>
                <div>
                  <Label>Assign to Batch</Label>
                  <Select value={form.batch_id} onValueChange={v => setForm(p => ({...p, batch_id: v}))}>
                    <SelectTrigger className="mt-1 bg-secondary/50 border-border/60"><SelectValue placeholder="Select batch (optional)" /></SelectTrigger>
                    <SelectContent className="bg-[#111827] border-border/60">
                      {batches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setDialog(null)}>Cancel</Button>
              <Button className="flex-1 bg-[#2BB5D8] hover:bg-[#25a3c4] text-[#0D1117] font-semibold" onClick={handleSave} disabled={saving}>
                {saving && <Loader2 size={14} className="animate-spin mr-1" />}
                {dialog === 'create' ? 'Create Template' : 'Save Changes'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={dialog === 'preview'} onOpenChange={v => !v && setDialog(null)}>
          <DialogContent className="bg-[#111827] border-border/60 max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader><DialogTitle>Template Preview</DialogTitle></DialogHeader>
            <div className="flex-1 overflow-y-auto rounded-lg border border-border/60">
              <iframe
                srcDoc={previewHtml}
                title="Template Preview"
                className="w-full min-h-[600px]"
                style={{ background: 'white' }}
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
